import { useState, useCallback, useMemo, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";

/**
 * Reusable Legal Page template. Renders a minimal, readable layout used by
 * Terms of Service, Privacy Policy, Return & Refund Policy.
 * Props:
 *  - title      : main H1 heading
 *  - subtitle   : short paragraph beneath the H1
 *  - lastUpdated: optional string shown above the content
 *  - metaTitle  : optional document.title override (SEO)
 *  - metaDescription : optional meta description (SEO)
 *  - children   : rich content (use <h2>, <p>, <ul><li>, <a> — styling applied by .legal-content)
 */
export default function LegalPage({
  title,
  subtitle,
  lastUpdated,
  metaTitle,
  metaDescription,
  children,
}) {
  const [dialog, setDialog] = useState({ open: false, type: "meeting" });
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  useEffect(() => {
    if (metaTitle) document.title = metaTitle;
    if (metaDescription) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", metaDescription);
    }
    window.scrollTo(0, 0);
  }, [metaTitle, metaDescription]);

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />

      <section className="section legal-hero" data-testid="legal-hero" style={{ paddingTop: 40 }}>
        <div className="section-inner" style={{ textAlign: "center", maxWidth: 820, margin: "0 auto" }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Legal</Reveal>
          <Reveal as="h1" delay={1} className="section-headline">{title}</Reveal>
          {subtitle && (
            <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, marginLeft: "auto", marginRight: "auto" }}>
              {subtitle}
            </Reveal>
          )}
        </div>
      </section>

      <section className="section legal-body" data-testid="legal-body">
        <div className="section-inner legal-content">
          {lastUpdated && <p className="legal-updated">Last Updated: {lastUpdated}</p>}
          {children}
        </div>
      </section>

      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source={`Legal · ${title}`} onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 500 } }} />
    </div>
  );
}
